import { ProductionUnit, formatNumber } from "@tosize/ui";
import type { IntlShape } from "react-intl";
import type { Shape as ThreeShape } from "three";
import { type ZodTypeAny, z } from "zod";
import type { SimpleProduct } from "../../types/product";
import type { Input, ShapeInput } from "../../types/shape";
import type { ThreeInputLinesType, ThreeSidePathsType } from "../../types/three";
import type { DimensionKeys, DimensionProperties } from "../../types/workpiece";
import { getMaxBasedOnUnit, getMoneyValue, parseNumber, parseToggleableNumber } from "../utils";
import type InputType from "./Input/InputType";
import { JsonInputType } from "./Input/JsonInputType";
import PriceType from "./Price/PriceType";
import type { SideName } from "./Side/SideName";

export default abstract class Shape {
	public static code: string;

	protected shapeInput: ShapeInput;
	protected unit?: ProductionUnit;
	protected product?: SimpleProduct;

	constructor(input: ShapeInput, unit?: ProductionUnit, product?: SimpleProduct) {
		this.shapeInput = input;
		this.unit = unit;
		this.product = product;
	}

	public abstract calculateSideLength(side: SideName, input: InputType): number | undefined;

	public abstract getDimensionsString(input: InputType, intl: IntlShape): string | undefined;

	/**
	 * Get constraints for remaining fields based on previous field values
	 */
	public abstract getConstraints(
		input: InputType,
		current: Input,
		equalCorners?: boolean,
	): number | undefined;

	/**
	 * @deprecated use the combination of `getThreeSidePaths` and `getThreeInputLines` instead
	 */
	public abstract getThreeShape(_: DimensionProperties[DimensionKeys]): undefined | ThreeShape;

	public abstract getThreeSidePaths(
		_: DimensionProperties[DimensionKeys],
	): undefined | ThreeSidePathsType[];

	public abstract getThreeInputLines(
		_: DimensionProperties[DimensionKeys],
	): undefined | ThreeInputLinesType[];

	public abstract getDimensions(
		_: DimensionProperties[DimensionKeys],
	): undefined | DimensionProperties[DimensionKeys];

	public formatInputs(
		inputs: InputType,
		intl: IntlShape,
		variant: "unit" | "decimal" = "unit",
	): InputType {
		let inputsKey: keyof InputType;
		const formattedInputs = {} as InputType;

		for (inputsKey in inputs) {
			if (!Number.isNaN(inputs[inputsKey]) && inputs[inputsKey] && inputs[inputsKey] !== "null") {
				const input = this.getInputById(inputsKey);
				const internalMultiplier = 10 ** (input?.precision ?? 0);
				const min = internalMultiplier === 1 ? 0 : 1;

				formattedInputs[inputsKey] = formatNumber({
					lang: intl.locale,
					value: Number(inputs[inputsKey]) / internalMultiplier,
					...(variant === "unit"
						? { style: "unit", unit: "centimeter", digits: { min, max: 1 } }
						: { style: "decimal", digits: { min, max: 1 } }),
				});
			}
		}

		return { ...inputs, ...formattedInputs };
	}

	public formattConstraint(input: string | number) {
		return Number(input !== "" ? input : 0);
	}

	public calculateSheetDimensions(input: InputType): { length: number; width: number } | undefined {
		const length = Number(input[JsonInputType.Length]);
		const width = Number(input[JsonInputType.Width]);

		if (!(Number.isNaN(length) || Number.isNaN(width))) {
			return { length, width };
		}
	}

	public calculateNeededM2(input: InputType): number {
		const dimensions = this.calculateSheetDimensions(input);

		return dimensions ? (dimensions.length * dimensions.width) / 1_000_000 : 0;
	}

	public calculatePrice(input: InputType, _quantity = 1, unit?: ProductionUnit): number {
		const neededM2 = this.calculateNeededM2(input);
		const dimensions = this.calculateSheetDimensions(input);

		const moneyM2 = this.product?.panelSizes?.find((panel) => panel.isDefault)?.priceM2;
		const priceM2 = moneyM2
			? getMoneyValue(moneyM2)
			: this.product
				? getMoneyValue(this.product.product_price_final)
				: 0;

		const panelSizes = this.product?.panelSizes?.filter((panel) =>
			dimensions ? panel.length >= dimensions.length && panel.width >= dimensions.width : false,
		);

		const moneyComplete = panelSizes?.length
			? panelSizes
					.map((panel) => getMoneyValue(panel.price))
					.reduce((prev, current) => (current < prev ? current : prev), Number.POSITIVE_INFINITY)
			: undefined;

		const priceComplete = moneyComplete ? moneyComplete : 0;

		const basePrice = unit === ProductionUnit.CompletePanel ? priceComplete : priceM2;

		let price = this.adjustPriceByType(neededM2 * basePrice);

		const threshold = this.shapeInput.prices.minimum_price_threshold
			? getMoneyValue(this.shapeInput.prices.minimum_price_threshold)
			: undefined;

		const minimum = ProductionUnit.CompletePanel
			? getMoneyValue(this.shapeInput.prices.minimum_price_complete_panel)
			: getMoneyValue(this.shapeInput.prices.minimum_price);

		if (!!threshold && neededM2 < threshold) {
			price += minimum;
		}

		return Math.max(price, minimum);
	}

	public getValidationSchema(): ZodTypeAny {
		return this.getSchemaRefinements(this.getDimensionSchema());
	}

	public getInputById(id: string): Input | undefined {
		return this.shapeInput.inputs.find((input) => input.id === id);
	}

	protected getSchemaRefinements(schema: ZodTypeAny): ZodTypeAny {
		return schema;
	}

	protected getDimensionSchema(): ZodTypeAny {
		const inputs: Record<string, ZodTypeAny> = {};

		for (const input of this.shapeInput.inputs) {
			const max = input.max ? getMaxBasedOnUnit(input.max, this.unit) : 0;
			let number = z.number().min(input.min).max(max);

			if (input.precision === 0) {
				number = number.int();
			}

			// Because react inputs always return strings, the value is first parsed to a number
			// using the zod preprocess and then validated after
			inputs[input.id] = z.preprocess(
				input.toggleable ? parseToggleableNumber : parseNumber,
				number,
			);
		}

		return z.object(inputs);
	}

	protected adjustPriceByType(price: number): number {
		const basePrice = getMoneyValue(this.shapeInput.prices.base_price);
		let adjustedPrice = price;

		switch (this.shapeInput.prices.base_price_type) {
			case PriceType.AddFixed:
				adjustedPrice += basePrice;
				break;
			case PriceType.AddPercentage:
				adjustedPrice *= 1 + basePrice / 100;
				break;
			case PriceType.SubtractFixed:
				adjustedPrice -= basePrice;
				break;
			case PriceType.SubtractPercentage:
				adjustedPrice *= 1 - basePrice / 100;
				break;
		}

		return adjustedPrice;
	}
}
